import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Only show fully supported languages
// Phase 1: English + Spanish complete
// Phase 2: Add other languages once fully translated
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇵🇷' },
];

// Reserved for future expansion (uncomment when translations are complete):
// { code: 'fr', name: 'Français', flag: '🇫🇷' },
// { code: 'pt', name: 'Português', flag: '🇵🇹' },
// { code: 'it', name: 'Italiano', flag: '🇮🇹' },
// { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
// { code: 'zh', name: '中文', flag: '🇨🇳' },
// { code: 'ko', name: '한국어', flag: '🇰🇷' },
// { code: 'ja', name: '日本語', flag: '🇯🇵' }

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  // Set during render (before children render) so labels read via <html lang> are never one render behind.
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('lang', language === 'es' ? 'es' : 'en');
  }

  useEffect(() => {
    localStorage.setItem('language', language);
    // WCAG 2.1 — keep <html lang="..."> in sync with the user's chosen language
    // so screen readers pronounce content with the correct phonetics.
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('lang', language === 'es' ? 'es' : 'en');
    }
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const changeLanguage = (langCode) => {
    setLanguage(langCode);
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    changeLanguage,
    isSpanish: language === 'es'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};